import { useEffect, useState } from 'react';
import { Banknote, ShoppingCart } from 'lucide-react';
import CreditPurchaseModal, { type PurchaseTab } from './CreditPurchaseModal';
import { MONEY } from '@kioskgaming/ui';

export interface CreditPurchaseTriggerProps {
  costRate?: number;
  variant?: 'card' | 'button';
  /** Button variant only: stack (dashboard card), row (responsive block), inline (toolbar with other controls). */
  buttonLayout?: 'row' | 'stack' | 'inline';
  className?: string;
  creditBalance?: number | null;
  creditBalanceLoading?: boolean;
  onlineBalanceNet?: number | null;
  onlineBalanceLoading?: boolean;
  onBalancesRefresh?: () => void | Promise<void>;
  conversionCostRate?: number | null;
  /** When true, navigates to checkout route on mount. */
  defaultOpen?: boolean;
  appearance?: 'light' | 'dark';
}

export default function CreditPurchaseTrigger({
  costRate,
  variant = 'card',
  buttonLayout = 'row',
  className = '',
  creditBalance,
  creditBalanceLoading,
  onlineBalanceNet,
  onlineBalanceLoading,
  onBalancesRefresh,
  conversionCostRate,
  defaultOpen = false,
  appearance = 'light',
}: CreditPurchaseTriggerProps) {
  const [open, setOpen] = useState(false);
  const [modalInitialTab, setModalInitialTab] = useState<PurchaseTab>('crypto');

  function openModal(tab: PurchaseTab) {
    setModalInitialTab(tab);
    setOpen(true);
  }

  useEffect(() => {
    if (!defaultOpen) return;
    setModalInitialTab('crypto');
    setOpen(true);
  }, [defaultOpen]);

  if (variant === 'button') {
    const btnBase =
      buttonLayout === 'inline'
        ? 'inline-flex shrink-0 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold shadow-sm'
        : 'inline-flex min-h-[42px] w-full flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-center text-sm font-semibold shadow-sm sm:min-h-[44px]';
    const buttonGroupClass =
      buttonLayout === 'stack'
        ? 'flex w-full flex-col gap-2'
        : buttonLayout === 'inline'
          ? 'flex flex-row flex-wrap items-center gap-2'
          : 'flex w-full flex-col gap-2 sm:flex-row sm:items-stretch';
    return (
      <>
        <div className={`${buttonGroupClass} ${className}`}>
          <button
            type="button"
            onClick={() => openModal('crypto')}
            className={`${btnBase} bg-blue-600 text-white shadow hover:bg-blue-700`}
          >
            <ShoppingCart className="h-4 w-4 shrink-0" aria-hidden />
            <span className="leading-snug">{MONEY.addUsd}</span>
          </button>
          <button
            type="button"
            onClick={() => openModal('balance')}
            className={`${btnBase} border border-blue-600/40 bg-white text-blue-800 hover:bg-blue-50`}
          >
            <Banknote className="h-4 w-4 shrink-0" aria-hidden />
            <span className="leading-snug">{MONEY.buyCredits}</span>
          </button>
        </div>
        <CreditPurchaseModal
          open={open}
          onClose={() => setOpen(false)}
          costRate={costRate}
          creditBalance={creditBalance}
          creditBalanceLoading={creditBalanceLoading}
          onlineBalanceNet={onlineBalanceNet}
          onlineBalanceLoading={onlineBalanceLoading}
          onBalancesRefresh={onBalancesRefresh}
          initialTab={modalInitialTab}
          conversionCostRate={conversionCostRate}
        />
      </>
    );
  }

  const dark = appearance === 'dark';
  const cardClass = dark
    ? `rounded-lg border border-zinc-700/90 bg-zinc-900/60 p-3.5 shadow-lg shadow-black/20 ${className}`
    : `rounded-lg border border-blue-100 bg-white p-3.5 shadow-sm ${className}`;

  return (
    <>
      <div className={cardClass}>
        <h2 className={`text-xs font-semibold ${dark ? 'text-zinc-100' : 'text-gray-900'}`}>{MONEY.addUsd}</h2>
        <p className={`mt-0.5 text-xs leading-snug ${dark ? 'text-zinc-500' : 'text-gray-500'}`}>
          Add USD with crypto, or turn your USD wallet balance into credits (no fee).
        </p>
        <div className="mt-2.5 flex w-full flex-col gap-1.5 sm:flex-row sm:items-stretch">
          <button
            type="button"
            onClick={() => openModal('crypto')}
            className={`inline-flex min-h-[34px] w-full flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-center text-xs font-semibold text-white shadow ${
              dark ? 'bg-blue-600 hover:bg-blue-500' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            <ShoppingCart className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="leading-snug">{MONEY.addUsd}</span>
          </button>
          <button
            type="button"
            onClick={() => openModal('balance')}
            className={`inline-flex min-h-[34px] w-full flex-1 items-center justify-center gap-1.5 rounded-md border px-3 py-1.5 text-center text-xs font-semibold shadow-sm ${
              dark
                ? 'border-blue-400/60 bg-zinc-800/80 text-blue-200 hover:bg-zinc-800'
                : 'border-blue-600/40 bg-white text-blue-800 hover:bg-blue-50'
            }`}
          >
            <Banknote className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="leading-snug">{MONEY.buyCredits}</span>
          </button>
        </div>
      </div>
      <CreditPurchaseModal
        open={open}
        onClose={() => setOpen(false)}
        costRate={costRate}
        creditBalance={creditBalance}
        creditBalanceLoading={creditBalanceLoading}
        onlineBalanceNet={onlineBalanceNet}
        onlineBalanceLoading={onlineBalanceLoading}
        onBalancesRefresh={onBalancesRefresh}
        initialTab={modalInitialTab}
        conversionCostRate={conversionCostRate}
      />
    </>
  );
}
